import React, { useContext } from 'react';
import { Checkbox, FormControlLabel, styled, Table, TableBody, TableCell, TableRow } from '@mui/material';
import CategorySelector from './CategorySelector';
import TagsSelector from './TagsSelector';
import { GameTag, GameCategory } from './gamedetailsform-graphql';
import { MAX_NO_TAGS } from '../GameBaseForm/baseFormHelper';
import { StyledBodyText, StyledWarningText } from '../../../../../../common/Styleguide/Common/Text';
import { StyledInput } from '../../../../../../common/Styleguide/Common/Input';
import { COLORS } from '../../../../../../common/Styleguide/Common/colors';
import { valueFromInput } from '../../../../../../common/input';
import { MAX_CONTROLS_LENGTH, MAX_DESCRIPTION_LENGTH } from './detailsFormHelper';
import { DOCS_URL } from '../../../../SideMenu/Menu';
import { GAME_COVERS_RESOLUTIONS, GameCoversWithUploadedFile } from '../../../../../../common/graphql/games/game';
import { UploadedFile } from '../../../../../../common/domain/upload';
import OrientationSelector from '../../../../../../common/CzyForm/OrientationSelector';
import MultiplayerLobbyEditor from '../GameBaseForm/MultiplayerLobbyEditor';
import VideoUpload from '../../../Admin/Game/VideoUpload';
import { SubmissionContext } from '../Context/SubmissionProvider';
import { getLatestBuild } from '../../../../../../common/build-utils';
import SubmissionUpdateGameCovers from '../../../Admin/Submission/UpdateSubmission/SubmissionUpdateGameCovers';
import { GameOrientation } from '@crazygames/game-types/src/game-build/orientation.type';
import { cleanAppStoreUrl, cleanPlaystoreUrl } from './utils';

export interface Props {
  tags: GameTag[];
  categories: GameCategory[];
}

export const InputTable = styled(Table)(({ theme }) => ({
  borderCollapse: 'collapse',
  maxWidth: '1000px',
  '& td': {
    padding: 0,
    paddingBottom: theme.spacing(1),
    margin: 0,
    borderBottom: 'none',
    verticalAlign: 'top',
  },
  '& td:first-of-type': {
    width: '200px',
  },
}));

/**
 * Renders the info form fields (game name, category, tags, description, controls, etc).
 * Updates this data, and uses it directly from Submission context.
 */
const GameDetailsForm: React.FC<Props> = ({ tags, categories }) => {
  const {
    submission,
    detailsForm,
    detailsFormProblems,
    updateDetailsForm,
    baseForm,
    updateBaseForm,
    baseFormProblems,
    isNonEditable,
    disableDetailsEditing,
    setVideoUploadProgress,
    attemptedToSave,
  } = useContext(SubmissionContext);
  const [originalAllowEmbed] = React.useState(detailsForm.allowEmbed);

  const renderOtherPlatforms = () => {
    const { playUrl, appstoreUrl, steamUrl, playStoreDownloads, appStoreDownloads, steamDownloads } = detailsForm;
    const invalidPlayUrl = detailsFormProblems?.includes('PLAY_URL_INVALID');
    const invalidStoreUrl = detailsFormProblems?.includes('APPSTORE_URL_INVALID');
    const invalidSteamUrl = detailsFormProblems?.includes('STEAM_URL_INVALID');
    const invalidPlayStoreDownloads = detailsFormProblems?.includes('PLAY_STORE_DOWNLOADS_INVALID');
    const invalidAppStoreDownloads = detailsFormProblems?.includes('APP_STORE_DOWNLOADS_INVALID');
    const invalidSteamDownloads = detailsFormProblems?.includes('STEAM_DOWNLOADS_INVALID');

    return (
      <Table sx={{ borderCollapse: 'collapse', td: { p: 0, pr: 1, pb: 1, m: 0, borderBottom: 'none' }, maxWidth: '1000px' }}>
        <TableBody>
          <TableRow>
            <TableCell>
              <StyledBodyText variant="h3" sx={{ display: 'flex' }} color="white80">
                Google Play Store
              </StyledBodyText>
            </TableCell>
            <TableCell>
              <StyledBodyText variant="h3" sx={{ display: 'flex' }} color="white80">
                iOS App Store
              </StyledBodyText>
            </TableCell>
            <TableCell>
              <StyledBodyText variant="h3" sx={{ display: 'flex' }} color="white80">
                Steam
              </StyledBodyText>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <StyledInput
                type="url"
                value={playUrl || ''}
                error={invalidPlayUrl}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
                  const playUrlOriginal = valueFromInput(evt);
                  const playUrl = playUrlOriginal ? cleanPlaystoreUrl(playUrlOriginal) : playUrlOriginal;
                  updateDetailsForm({ ...detailsForm, playUrl });
                }}
                placeholder="Link"
              />
              {invalidPlayUrl && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid Google Play Store link`}
                </StyledBodyText>
              )}
            </TableCell>
            <TableCell>
              <StyledInput
                type="url"
                value={appstoreUrl || ''}
                error={invalidStoreUrl}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.FocusEvent<HTMLInputElement>) => {
                  const appstoreUrlOriginal = valueFromInput(evt);
                  const appstoreUrl = appstoreUrlOriginal ? cleanAppStoreUrl(appstoreUrlOriginal) : appstoreUrlOriginal;
                  updateDetailsForm({ ...detailsForm, appstoreUrl });
                }}
                placeholder="Link"
              />
              {invalidStoreUrl && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid IOS App Store link`}
                </StyledBodyText>
              )}
            </TableCell>
            <TableCell>
              <StyledInput
                type="url"
                defaultValue={steamUrl}
                error={invalidSteamUrl}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.FocusEvent<HTMLInputElement>) => {
                  const steamUrl = valueFromInput(evt);
                  updateDetailsForm({ ...detailsForm, steamUrl });
                }}
                placeholder="Link"
              />
              {invalidSteamUrl && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid Steam link`}
                </StyledBodyText>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>
              <StyledInput
                defaultValue={playStoreDownloads}
                error={invalidPlayStoreDownloads}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.FocusEvent<HTMLInputElement>) => {
                  const playStoreDownloads = valueFromInput(evt);
                  updateDetailsForm({ ...detailsForm, playStoreDownloads });
                }}
                placeholder="# Downloads"
              />
              {invalidPlayStoreDownloads && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid downloads number`}
                </StyledBodyText>
              )}
            </TableCell>
            <TableCell>
              <StyledInput
                defaultValue={appStoreDownloads}
                error={invalidAppStoreDownloads}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.FocusEvent<HTMLInputElement>) => {
                  const appStoreDownloads = valueFromInput(evt);
                  updateDetailsForm({ ...detailsForm, appStoreDownloads });
                }}
                placeholder="# Downloads"
              />
              {invalidAppStoreDownloads && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid downloads number`}
                </StyledBodyText>
              )}
            </TableCell>
            <TableCell>
              <StyledInput
                defaultValue={steamDownloads}
                error={invalidSteamDownloads}
                disabled={disableDetailsEditing}
                required={false}
                onChange={(evt: React.FocusEvent<HTMLInputElement>) => {
                  const steamDownloads = valueFromInput(evt);
                  updateDetailsForm({ ...detailsForm, steamDownloads });
                }}
                placeholder="# Downloads"
              />
              {invalidSteamDownloads && (
                <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
                  {`Invalid downloads number`}
                </StyledBodyText>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderCategory = () => {
    const { category: selectedCategory } = detailsForm;
    const categoryError = attemptedToSave && detailsFormProblems?.includes('CATEGORY_MISSING');
    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ mt: 3, mb: 1 }} color="white80">
            Category <span>*</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <CategorySelector
            disabled={disableDetailsEditing}
            error={categoryError}
            categories={categories}
            selected={selectedCategory}
            onChange={(category: string | null) => {
              updateDetailsForm({ ...detailsForm, category });
            }}
          />
          {categoryError && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please select a category`}
            </StyledBodyText>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderTags = () => {
    const { tags: selectedTags } = detailsForm;
    const tagError = attemptedToSave && detailsFormProblems?.includes('TAG_MISSING');
    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ marginY: 1.25 }}>
            Tags <span>*</span>{' '}
            <span style={{ fontSize: 12, color: COLORS.white[50], fontWeight: 400, marginLeft: 8 }}>MAX. {MAX_NO_TAGS}</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <TagsSelector
            error={tagError}
            tags={tags}
            selected={selectedTags}
            onChange={(tags: string[]) => {
              updateDetailsForm({ ...detailsForm, tags });
            }}
            disabled={disableDetailsEditing}
          />
          {tagError && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please select at least 1 tag`}
            </StyledBodyText>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderDescription = () => {
    const { description } = detailsForm;
    const descriptionMissing = attemptedToSave && detailsFormProblems?.includes('DESCRIPTION_MISSING');
    const descriptionTooLong = detailsFormProblems?.includes('DESCRIPTION_TOO_LONG');
    const descriptionError = descriptionMissing || descriptionTooLong;

    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ marginY: 1.25 }} color="white80">
            Description <span>*</span>{' '}
            <span style={{ fontSize: 12, color: COLORS.white[50], fontWeight: 400, marginLeft: 8 }}>NO HTML ALLOWED</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <StyledInput
            value={description || ''}
            error={descriptionError}
            disabled={disableDetailsEditing}
            required={true}
            multiline={true}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              const description = valueFromInput(evt);
              updateDetailsForm({ ...detailsForm, description });
            }}
          />
          {descriptionMissing && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please fill in the input `}
            </StyledBodyText>
          )}
          {descriptionTooLong && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please limit your input to ${MAX_DESCRIPTION_LENGTH} characters`}
            </StyledBodyText>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderControls = () => {
    const { controls } = detailsForm;
    const controlMissing = attemptedToSave && detailsFormProblems?.includes('CONTROLS_MISSING');
    const controlTooLong = detailsFormProblems?.includes('CONTROLS_TOO_LONG');
    const controlError = controlMissing || controlTooLong;

    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ marginY: 1.25 }}>
            Controls <span>*</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <StyledInput
            error={controlError}
            value={controls || ''}
            disabled={disableDetailsEditing}
            required={true}
            multiline={true}
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              const controls = valueFromInput(evt);
              updateDetailsForm({ ...detailsForm, controls });
            }}
          />
          {controlMissing && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please fill in the input `}
            </StyledBodyText>
          )}
          {controlTooLong && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please limit your input to ${MAX_CONTROLS_LENGTH} characters`}
            </StyledBodyText>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderCoverImageUpload = () => {
    const coverError16x9 = attemptedToSave && detailsFormProblems?.includes('COVER_IMAGE_MISSING16x9');
    const coverError2x3 = attemptedToSave && detailsFormProblems?.includes('COVER_IMAGE_MISSING2x3');
    const coverError1x1 = attemptedToSave && detailsFormProblems?.includes('COVER_IMAGE_MISSING1x1');
    const anyCoverError = coverError16x9 || coverError1x1 || coverError2x3;

    const errors = {
      '16x9': !!coverError16x9,
      '2x3': !!coverError2x3,
      '1x1': !!coverError1x1,
    };

    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ marginY: 1.25 }}>
            Cover images <span>*</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <SubmissionUpdateGameCovers
            gameCovers={detailsForm.gameCovers}
            errors={errors}
            onChange={(coverId: GAME_COVERS_RESOLUTIONS, file: UploadedFile | null) => {
              updateDetailsForm({
                ...detailsForm,
                gameCovers: { ...detailsForm.gameCovers, [coverId]: file } as GameCoversWithUploadedFile,
              });
            }}
            isNonEditable={isNonEditable}
          />
          {anyCoverError && (
            <StyledBodyText sx={{ fontSize: 12, m: 0.5 }} variant="bodyLower" color="error">
              {`Please upload a cover image for each aspect ratio`}
            </StyledBodyText>
          )}
          <StyledBodyText variant="bodyLower" color="white30">
            We will use the cover image to show your game on our pages (homepage, category pages, …). Make it appealing and professional
            looking! A good cover image will make the users want to play your game. Please provide 3 sizes for your game cover: landscape
            (1920x1080) portrait (800x1200) and square (800x800). For more information, make sure to read our{` `}
            <a href={`${DOCS_URL}/requirements/game-covers/`} target="_blank" rel="noreferrer">
              guidelines for game covers
            </a>
            .
          </StyledBodyText>
        </TableCell>
      </TableRow>
    );
  };

  const renderVideoUpload = () => {
    if (!submission) {
      return;
    }
    const { portraitVideoOriginal, videoOriginal } = detailsForm.videos;
    const { slug } = submission;

    const videoStillUploadingError = detailsFormProblems?.includes('VIDEO_UPLOADING');
    const landscapeVideoMissing = detailsFormProblems?.includes('LANDSCAPE_VIDEO_MISSING');

    return (
      <TableRow>
        <TableCell>
          <StyledBodyText variant="h3" sx={{ marginY: 1.25 }}>
            Preview videos <span>*</span>
          </StyledBodyText>
        </TableCell>
        <TableCell>
          <div style={{ display: 'flex', flexDirection: 'row', gap: 24 }}>
            <VideoUpload
              originalVideo={videoOriginal}
              slug={slug}
              onUploadStatusChange={(status) => {
                setVideoUploadProgress('landscape', status);
              }}
              error={landscapeVideoMissing && attemptedToSave ? 'Please upload a landscape video' : ''}
              onChange={(thumbnailLocation, originalLocation) => {
                updateDetailsForm({
                  ...detailsForm,
                  videos: {
                    ...detailsForm.videos,
                    videoOriginal: originalLocation,
                    videoThumbnail: thumbnailLocation,
                  },
                });
              }}
              label={'Landscape video *'}
              mandatory={true}
              videoContainerRatio={{ width: 180, height: 112 }}
              isNonEditable={isNonEditable}
            />
            <VideoUpload
              originalVideo={portraitVideoOriginal}
              slug={slug}
              onUploadStatusChange={(status) => {
                setVideoUploadProgress('portrait', status);
              }}
              onChange={(thumbnailLocation, originalLocation) => {
                updateDetailsForm({
                  ...detailsForm,
                  videos: {
                    ...detailsForm.videos,
                    portraitVideoOriginal: originalLocation,
                    portraitVideoThumbnail: thumbnailLocation,
                  },
                });
              }}
              label={'Portrait video'}
              videoContainerRatio={{ width: 112, height: 180 }}
              isNonEditable={isNonEditable}
            />
          </div>
          <StyledBodyText variant="bodyLower" color="white30" sx={{ mb: 3 }}>
            Game videos will be displayed when players hover over your game cover. A compelling game video is crucial - it's often the first
            impression that captures attention and drives players to click and play. So make sure to read our{' '}
            <a href={`${DOCS_URL}/requirements/game-covers/#preview-video`} target="_blank" rel="noreferrer">
              guidelines
            </a>
            . We reserve the right to replace the game video if needed.
          </StyledBodyText>
          <StyledBodyText variant="bodyLower" color="white30" sx={{ mb: 3 }}>
            Supported formats: MP4, MOV. Max length 20 seconds.
          </StyledBodyText>
          {videoStillUploadingError && attemptedToSave && (
            <StyledBodyText sx={{ m: 0.5 }} variant="bodyLower2" color="error">
              Please wait for video upload to complete
            </StyledBodyText>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderMobileOrientation = () => {
    const { isIOSFriendly, isAndroidFriendly, orientation } = baseForm;
    const orientationError = attemptedToSave && baseFormProblems?.includes('ORIENTATION_MISSING');

    return (
      (isIOSFriendly || isAndroidFriendly) && (
        <TableRow>
          <TableCell>
            <StyledBodyText variant="h3" sx={{ mt: 1.25, mb: 1.25 }}>
              Mobile orientation <span>*</span>
            </StyledBodyText>
          </TableCell>
          <TableCell>
            <OrientationSelector
              disabled={isNonEditable}
              error={orientationError}
              defaultValue={orientation}
              onChange={(orientation: string | null) => {
                updateBaseForm({ ...baseForm, orientation: orientation as GameOrientation | null });
              }}
            />
            {orientationError && (
              <StyledBodyText sx={{ m: 0.5, height: 20 }} variant="bodyLower2" color="error">
                Please select a orientation
              </StyledBodyText>
            )}
            <StyledBodyText variant="bodyLower" sx={{ marginBlock: 0 }} color="white30">
              Note: the orientation locking will be managed by us.
            </StyledBodyText>
          </TableCell>
        </TableRow>
      )
    );
  };

  const renderFullscreen = () => {
    // To keep things simply the dev is presented just with a "Supports fullscreen" checkbox. However, on the backend this is translated to multiple options.
    const { fullscreen } = baseForm;
    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={fullscreen !== 'NOT_SUPPORTED'}
              onChange={(_evt: {}, isFullscreenable: boolean) => {
                updateBaseForm({ ...baseForm, fullscreen: isFullscreenable ? 'SUPPORTED' : 'NOT_SUPPORTED' });
              }}
              disabled={disableDetailsEditing}
            />
          }
          label="The game works well in fullscreen"
          sx={{ color: COLORS.black[10] }}
        />
      </div>
    );
  };

  const renderAllowEmbed = () => {
    const { allowEmbed } = detailsForm;
    if (!originalAllowEmbed) {
      // new submissions won't see this toggle anymore
      // existing submissions that ticked it still see it, but if they untick it, it will not be shown again
      return null;
    }
    const hasSDK = getLatestBuild(submission?.gameBuilds || [])?.qaResult?.SDKImplemented;
    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={allowEmbed}
              onChange={(_evt: {}, allowEmbed: boolean) => {
                updateDetailsForm({ ...detailsForm, allowEmbed });
              }}
              disabled={disableDetailsEditing}
            />
          }
          label="Allow non-CrazyGames websites to embed your game (without revenue share)"
          sx={{ color: COLORS.black[10] }}
        />
        {allowEmbed && hasSDK && (
          <StyledWarningText>
            Various SDK functionality (user accounts, saving data, payments) is not supported on external websites.
          </StyledWarningText>
        )}
      </div>
    );
  };

  return (
    <>
      <InputTable>
        <TableBody>
          {renderCategory()}
          {renderTags()}
          {renderDescription()}
          {renderControls()}
        </TableBody>
      </InputTable>
      {renderOtherPlatforms()}
      <InputTable>
        <TableBody>
          {renderCoverImageUpload()}
          {renderVideoUpload()}
          {renderMobileOrientation()}
        </TableBody>
      </InputTable>
      {renderFullscreen()}
      {renderAllowEmbed()}
      <MultiplayerLobbyEditor />
    </>
  );
};

export default GameDetailsForm;
